import React, {
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { ExtensionEndpoint as ExtensionEndpointLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { AgentFields } from '../../../NonReact/constants/AgentConstants'
import { CXScoreLicences } from '../../../NonReact/constants/CXScoreLicences'
import { DeviceFields } from '../../../NonReact/constants/DeviceConstants'
import { ALL_GROUPS, DEVICE_FILTER_EXTENSIONS_ONLY } from '../../../NonReact/constants/DeviceTypes'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { IsStringEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { RESTAPIError } from '../../Common/API'
import {
    ALL_VALUE,
    getAgentsFromPartition,
    getDevicesFromPartition,
    getPartitionsForTelServer,
    getTelServersAsSelectOptions,
    INVALID_VALUE
} from '../../Common/API/ItemsGetters'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import FormRow from '../../Common/Components/FormRow'

import { InputHelp } from '../../Common/Components/InputHelp/InputHelp'
import { SelectOptions } from '../../Common/Components/Selects'
import {
    AgentSelectItem,
    ComboBoxItem,
    DeviceSelectItem,
    SelectItem,
    SelectOption
} from '../../Common/Components/Selects/Types'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { ReactState, Rules, StateSetter } from '../../Common/Types/Types'
import { indicateUnmonitoredItems } from '../../Common/Utils/ItemsResponseUtils'
import { handleUnknownForAvailables, removeUnknownOption } from '../AddUserFunctions'
import { ENDPOINT_FIELD_KIND, SERVER_ALL_VALUE, SERVER_NONE_VALUE, SERVER_UNKNOWN_VALUE } from '../Constants'
import {
    AGENTS_PERM_NAME,
    CME_PERM_NAME,
    CXS_SUBSCRIPTION_NAME,
    DEVICES_PERM_NAME,
    ENDPOINT_AGENT_NAME,
    ENDPOINT_DEVICE_NAME,
    ENDPOINT_PARTITION_NAME,
    ENDPOINT_TEL_SYS_NAME,
    PARTITION_PERM_NAME,
    REPORTING_LEVEL_NAME,
    TEL_SYS_PERM_NAME
} from '../FieldNames'
import { ReportingAccessLevels } from '../Reporting/ReportingAccessLevels'
import { ApplicationUserFormData } from '../Types'
import { ExtensionEndpointsDefaults } from './ExtensionEndpointDefaults'
import { getDeviceFields } from '../Permissions/Permissions'
import { licenceEndpointDeviceRequiredRule } from '../Rules/ExtensionEndpointRules'
import { getBusinessAwareValidationMessage } from '../Rules/UserLicenceValidationMessages'
import { IntegrationType } from '../../TelSysConfiguration/Constants/Constants'

export type FormEndpoint = ApplicationUserFormData['extensionendpoint']

type Props = {
    endpoint?: FormEndpoint
    permissionAvailableDevice: ReactState<DeviceSelectItem[]>
    permissionAvailableAgents: ReactState<SelectItem[]>
    endpointAvailableDevices: ReactState<DeviceSelectItem[]>
    endpointAvailableAgents: ReactState<AgentSelectItem[]>
    allAvailableTelServers: SelectOptions<number>
    setAllAvailableTelServers: StateSetter<SelectOptions<number>>
    setAvailablePartitions: StateSetter<SelectOptions<number>>
    availablePartitions: SelectOptions<number>
    setUserdetailsFromDevice: (device: string) => void
    setUserdetailsFromAgent: (agent: string) => void
    viewOnly?: boolean
    telsysType?: Nullable<TelSysTypes>
    setAttachedDevice: StateSetter<Nullable<DeviceSelectItem>>
    setAvailableTelServers: StateSetter<SelectOptions<number>>
    availableTelServers: SelectOptions<number>
    isWebexAgentOrSupervisorUser: boolean
    isCxsSubscriptionApplicable: boolean
    isCxrSubscriptionApplicable: boolean
    integrationType?: Nullable<IntegrationType>
}

/**
 * Extension Endpoint details for the user.
 *
 * @param endpoint - The endpoint as a form structure.
 * @param permissionAvailableDevice - Available devices for permissions.
 * @param permissionAvailableAgents - Available agents for permissions.
 */
const ExtensionEndpoint: React.FC<Props> = ({
    endpoint,
    permissionAvailableDevice: [permissionAvailableDevices],
    permissionAvailableAgents: [permissionAvailableAgents],
    endpointAvailableDevices: [availableDevices, setAvailableDevices],
    endpointAvailableAgents: [availableAgents, setAvailableAgents],
    setUserdetailsFromDevice,
    setUserdetailsFromAgent,
    viewOnly,
    telsysType,
    setAttachedDevice,
    setAllAvailableTelServers,
    allAvailableTelServers,
    setAvailableTelServers,
    setAvailablePartitions,
    availableTelServers,
    availablePartitions,
    isWebexAgentOrSupervisorUser,
    isCxsSubscriptionApplicable,
    isCxrSubscriptionApplicable,
    integrationType
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('ExtensionEndpoint')

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const telsysTypeRef: MutableRefObject<Nullable<TelSysTypes>> = useRef<Nullable<TelSysTypes>>(telsysType)

    const endpointDefaults: ExtensionEndpointsDefaults = ExtensionEndpointsDefaults.getInstance(language)

    const { displayError }: MessageContextType = useContext(MessageContext)
    const { watch, setValue, trigger } = useFormContext()

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingDevices, setIsLoadingDevices]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingAgents, setIsLoadingAgents]: ReactState<boolean> = useState<boolean>(false)

    const isLoading: boolean = isLoadingTelServers || isLoadingPartitions || isLoadingDevices || isLoadingAgents

    const cmeValue: string = watch(CME_PERM_NAME)
    const telSysValue: Nullable<number> = watch(TEL_SYS_PERM_NAME)
    const partitionValue: Nullable<number[]> = watch(PARTITION_PERM_NAME)
    const deviceValue: string = watch(DEVICES_PERM_NAME)
    const agentValue: string = watch(AGENTS_PERM_NAME)

    const endpointTelSysValue: number = watch(ENDPOINT_TEL_SYS_NAME)
    const endpointPartitionValue: number = watch(ENDPOINT_PARTITION_NAME)
    const endpointDeviceValue: string = watch(ENDPOINT_DEVICE_NAME)
    const endpointAgentsValue: string = watch(ENDPOINT_AGENT_NAME)

    const reportingLevelValue: string = watch(REPORTING_LEVEL_NAME)

    const isProfessionalUser: boolean = useMemo(
        (): boolean => reportingLevelValue === ReportingAccessLevels.PROFESSIONAL,
        [reportingLevelValue]
    )
    const isAgentUser: boolean = reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT

    const cxsSubscriptionValue: string = watch(CXS_SUBSCRIPTION_NAME)

    // A Real-Time User (Professional) holds a device endpoint and has its agent field disabled, and the backend
    // rejects a CXS Agent licence for that tier anyway. An untouched value loaded from an existing user escapes
    // the Details tab normalisation, so the CXS rules are treated as inapplicable here rather than disabling the
    // device this level requires and demanding an agent through a field the operator cannot use.
    const isCxsRuleApplicable: boolean = isCxsSubscriptionApplicable && !isProfessionalUser

    // A CXS Agent licence scores the user's own agent, so the backend requires the user to resolve to one and
    // rejects an endpoint device outright (error 11414). The device is therefore not an option for this
    // subscription, and the field is disabled the same way it is for an Agent-type user (APD-13455).
    const isCxsAgentSubscription: boolean = isCxsRuleApplicable && cxsSubscriptionValue === CXScoreLicences.CXS_AGENT

    const isEndpointDeviceDisabled: boolean = viewOnly || isAgentUser || isCxsAgentSubscription

    const isEnterpriseParitionUser: boolean = useMemo(
        (): boolean =>
            reportingLevelValue === ReportingAccessLevels.ESSENTIALS ||
            reportingLevelValue === ReportingAccessLevels.PROFESSIONAL ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT ||
            reportingLevelValue === ReportingAccessLevels.ENTERPRISE ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
        [reportingLevelValue]
    )

    // Create an error handler for an individual loader.
    const handleError =
        (...loadingSetters: StateSetter<boolean>[]): ((e: RESTAPIError) => void) =>
        (e: RESTAPIError): void => {
            if (isMounted.current) {
                loadingSetters.forEach((loadingSetter: StateSetter<boolean>): void => loadingSetter(false))
            }

            displayError(e)
        }

    useEffect((): void => {
        if (isMounted.current) {
            if (isEnterpriseParitionUser) {
                setAvailableTelServers(
                    allAvailableTelServers.filter(
                        (availableTelServer: SelectOption<number>): boolean =>
                            !!availableTelServer.licences?.includes(ReportingAccessLevels.ENTERPRISE)
                    )
                )
            } else {
                setAvailableTelServers(allAvailableTelServers)
            }
        }
    }, [allAvailableTelServers, isEnterpriseParitionUser])

    useEffect((): void => {
        handleUnknownForAvailables([], setAllAvailableTelServers, endpointTelSysValue)
        handleUnknownForAvailables([], setAvailablePartitions, endpointPartitionValue)
        handleUnknownForAvailables([], setAvailableDevices, endpointDeviceValue)
        handleUnknownForAvailables([], setAvailableAgents, endpointAgentsValue)

        if (cmeValue && !IsStringEmpty(cmeValue) && cmeValue !== SERVER_UNKNOWN_VALUE) {
            setIsLoadingTelServers(true)

            getTelServersAsSelectOptions(cmeValue, true, false)
                .then((telServers: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingTelServers(false)
                        handleUnknownForAvailables(telServers, setAllAvailableTelServers, endpointTelSysValue)
                    }
                })
                .catch(handleError(setIsLoadingTelServers))
        }
    }, [cmeValue])

    useEffect((): void => {
        if (endpoint) {
            const { telephonyserver }: FormEndpoint = endpoint

            if (telephonyserver && telephonyserver !== ALL_VALUE && telephonyserver !== INVALID_VALUE) {
                setIsLoadingPartitions(true)
                getPartitionsForTelServer(telephonyserver)
                    .then((partitions: SelectOptions<number>): void => {
                        if (isMounted.current) {
                            setIsLoadingPartitions(false)
                            handleUnknownForAvailables(partitions, setAvailablePartitions, endpointPartitionValue)
                        }
                    })
                    .catch(handleError(setIsLoadingPartitions))
            } else {
                handleUnknownForAvailables([], setAvailablePartitions, endpointPartitionValue)
            }
        }
    }, [endpoint])

    useEffect((): void => {
        // What to do when the partition value changes.
        if (!IsVarEmpty(endpointTelSysValue)) {
            if (endpointTelSysValue >= 0 && endpointTelSysValue !== INVALID_VALUE) {
                setIsLoadingPartitions(true)
                getPartitionsForTelServer(endpointTelSysValue)
                    .then((partitions: SelectOptions<number>): void => {
                        if (isMounted.current) {
                            setIsLoadingPartitions(false)
                            handleUnknownForAvailables(partitions, setAvailablePartitions, endpointPartitionValue)
                        }
                    })
                    .catch(handleError(setIsLoadingPartitions))
            } else {
                handleUnknownForAvailables([], setAvailablePartitions, endpointPartitionValue)
                handleUnknownForAvailables([], setAvailableDevices, endpointDeviceValue)
                handleUnknownForAvailables([], setAvailableAgents, endpointAgentsValue)
            }
        }

        removeUnknownOption(setAllAvailableTelServers, endpointTelSysValue)
    }, [endpointTelSysValue])

    useEffect((): void => {
        // What to do when the partition value changes.
        if (!IsVarEmpty(endpointPartitionValue)) {
            if (
                endpointTelSysValue !== ALL_VALUE &&
                endpointPartitionValue !== ALL_VALUE &&
                endpointTelSysValue !== INVALID_VALUE &&
                endpointPartitionValue !== INVALID_VALUE
            ) {
                const isDevicePerm: boolean =
                    !IsStringEmpty(deviceValue) &&
                    deviceValue !== SERVER_ALL_VALUE &&
                    deviceValue !== SERVER_UNKNOWN_VALUE
                const isDeviceEndpoint: boolean =
                    !IsStringEmpty(endpointDeviceValue) &&
                    endpointDeviceValue !== SERVER_NONE_VALUE &&
                    endpointDeviceValue !== SERVER_UNKNOWN_VALUE
                const isAgentPerm: boolean =
                    !IsStringEmpty(agentValue) && agentValue !== SERVER_ALL_VALUE && agentValue !== SERVER_UNKNOWN_VALUE

                setIsLoadingDevices(true)
                setIsLoadingAgents(true)

                indicateUnmonitoredItems(
                    getDevicesFromPartition(
                        endpointTelSysValue,
                        endpointPartitionValue,
                        false,
                        DEVICE_FILTER_EXTENSIONS_ONLY,
                        isDevicePerm && isDeviceEndpoint ? deviceValue : undefined,
                        undefined,
                        undefined,
                        undefined,
                        DeviceFields.TELSYS_USERNAME,
                        DeviceFields.OVERRIDE_MONITORING,
                        ...getDeviceFields(telsysTypeRef.current ?? TelSysTypes.UNKNOWN)
                    ).then(([devices]: [DeviceSelectItem[], Definable<number>]): DeviceSelectItem[] => devices)
                )
                    .then((devices: DeviceSelectItem[]): void => {
                        if (isMounted.current) {
                            setIsLoadingDevices(false)
                            if (isDevicePerm && devices?.length === 0) {
                                handleUnknownForAvailables(
                                    permissionAvailableDevices.filter(
                                        (availableDevice: DeviceSelectItem): boolean =>
                                            availableDevice.value === deviceValue &&
                                            !ALL_GROUPS.includes(availableDevice.type ?? '')
                                    ),
                                    setAvailableDevices,
                                    endpointDeviceValue
                                )
                            } else {
                                handleUnknownForAvailables(devices, setAvailableDevices, endpointDeviceValue)
                            }
                        }
                    })
                    .catch(handleError(setIsLoadingDevices))

                if (isAgentPerm) {
                    handleUnknownForAvailables(
                        permissionAvailableAgents.filter(
                            (availableAgent: SelectItem): boolean => availableAgent.value === agentValue
                        ),
                        setAvailableAgents,
                        endpointAgentsValue
                    )
                    setIsLoadingAgents(false)
                } else {
                    indicateUnmonitoredItems(
                        getAgentsFromPartition(
                            endpointTelSysValue,
                            endpointPartitionValue,
                            false,
                            DEVICE_FILTER_EXTENSIONS_ONLY,
                            !isDevicePerm ||
                                !ALL_GROUPS.includes(
                                    permissionAvailableDevices.find(
                                        (availableDevice: DeviceSelectItem): boolean =>
                                            availableDevice.value === deviceValue
                                    )?.type ?? ''
                                )
                                ? undefined
                                : deviceValue,
                            AgentFields.ID_ON_TEL_SYS,
                            AgentFields.TELSYS_USERNAME,
                            AgentFields.OVERRIDE_MONITORING
                        )
                    )
                        .then((agents: AgentSelectItem[]): void => {
                            if (isMounted.current) {
                                setIsLoadingAgents(false)
                                handleUnknownForAvailables(agents, setAvailableAgents, endpointAgentsValue)
                            }
                        })
                        .catch(handleError(setIsLoadingAgents))
                }
            } else {
                handleUnknownForAvailables([], setAvailableDevices, endpointDeviceValue)
                handleUnknownForAvailables([], setAvailableAgents, endpointAgentsValue)
            }
        }
    }, [
        endpointPartitionValue,
        deviceValue,
        agentValue,
        permissionAvailableDevices,
        permissionAvailableAgents,
        telsysTypeRef.current
    ])

    useEffect((): void => {
        removeUnknownOption(setAvailablePartitions, endpointPartitionValue)
    }, [endpointPartitionValue])

    useEffect((): void => {
        removeUnknownOption(setAvailableDevices, endpointDeviceValue)
        setAttachedDevice(
            availableDevices.find((device: DeviceSelectItem): boolean => device.value === endpointDeviceValue) ??
                undefined
        )
    }, [endpointDeviceValue])

    useEffect((): void => {
        removeUnknownOption(setAvailableAgents, endpointAgentsValue)
    }, [endpointAgentsValue])

    useEffect((): void => {
        telsysTypeRef.current = telsysType
    }, [telsysType])

    useEffect((): void => {
        if (!isCxsAgentSubscription) return

        if (IsStringEmpty(endpointDeviceValue) || endpointDeviceValue === SERVER_NONE_VALUE) return

        // The device field is disabled for a CXS Agent subscription, so a device left over from an earlier
        // selection could not be cleared by hand and would still be submitted against the licence.
        setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
        trigger(ENDPOINT_AGENT_NAME)
    }, [isCxsAgentSubscription, endpointDeviceValue])

    const validateIsProfessional = useCallback(
        (value: string): ValidateResult => {
            const rptLevelValue: string = watch(REPORTING_LEVEL_NAME)
            const isProUser: boolean = rptLevelValue === ReportingAccessLevels.PROFESSIONAL

            // Treat any unselected device as missing. The form represents "no device" inconsistently
            // (null when switching level, the 'None' sentinel elsewhere), so a strict SERVER_NONE_VALUE
            // check would miss the null case and the required indicator would never fire on the switch.
            if ((IsVarEmpty(value) || value === SERVER_NONE_VALUE) && isProUser) {
                return getBusinessAwareValidationMessage(
                    language,
                    ExtensionEndpointLanguage.ExtensionEndpoint_Validation_Professional_Device,
                    telsysTypeRef.current,
                    integrationType
                )
            }

            return undefined
        },
        [watch(REPORTING_LEVEL_NAME), telsysTypeRef, integrationType]
    )

    const validateIsAgentUser = useCallback(
        (value: string): ValidateResult => {
            const rptLevelValue: string = watch(REPORTING_LEVEL_NAME)
            const isAgentLevelUser: boolean = rptLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT

            if (value === SERVER_NONE_VALUE && isAgentLevelUser) {
                return getBusinessAwareValidationMessage(
                    language,
                    ExtensionEndpointLanguage.ExtensionEndpoint_Validation_CallCentreAgent_Agent,
                    telsysTypeRef.current,
                    integrationType
                )
            }

            return undefined
        },
        [watch(REPORTING_LEVEL_NAME), telsysTypeRef, integrationType]
    )

    const deviceRules: Rules = {
        validate: {
            isProfessional: validateIsProfessional,
            licenceDeviceLink: licenceEndpointDeviceRequiredRule({
                language,
                watch,
                field: ENDPOINT_FIELD_KIND.DEVICE,
                isCxsSubscriptionApplicable: isCxsRuleApplicable,
                isCxrSubscriptionApplicable
            })
        }
    }

    const agentRules: Rules = {
        validate: {
            validateIsAgentUser,
            licenceDeviceLink: licenceEndpointDeviceRequiredRule({
                language,
                watch,
                field: ENDPOINT_FIELD_KIND.AGENT,
                isCxsSubscriptionApplicable: isCxsRuleApplicable,
                isCxrSubscriptionApplicable
            })
        }
    }

    return (
        <div className='endpoint-tab tab-area'>
            <FormRow className='telephony-server'>
                <ComboBox
                    variant='outlined'
                    name={ENDPOINT_TEL_SYS_NAME}
                    label={language.get(ExtensionEndpointLanguage.ExtensionEndpoint_TelServer)}
                    loading={isLoadingTelServers}
                    defaultOption={endpointDefaults.getNoneTelServersOption()}
                    options={availableTelServers}
                    disabled={telSysValue !== ALL_VALUE || viewOnly}
                    onChange={(): void => {
                        setValue(ENDPOINT_PARTITION_NAME, ALL_VALUE)
                        setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
                        setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)
                    }}
                    noChange={isLoading}
                />
            </FormRow>
            <FormRow className='partition'>
                <ComboBox
                    variant='outlined'
                    name={ENDPOINT_PARTITION_NAME}
                    label={language.get(ExtensionEndpointLanguage.ExtensionEndpoint_Partition)}
                    loading={isLoadingPartitions}
                    defaultOption={endpointDefaults.getNonePartitionOption()}
                    options={availablePartitions
                        .filter(
                            (ap: SelectOption<number>): boolean =>
                                !!(
                                    !partitionValue ||
                                    partitionValue?.includes(ALL_VALUE) ||
                                    partitionValue?.includes(ap.value)
                                )
                        )
                        .filter((ap: SelectOption<number>): boolean => {
                            if (!isEnterpriseParitionUser) {
                                return true
                            }
                            return ap.licencedForRptType === ReportingAccessLevels.ENTERPRISE
                        })}
                    disabled={
                        !!(partitionValue && partitionValue.length === 1 && partitionValue[0] !== ALL_VALUE) || viewOnly
                    }
                    onChange={(): void => {
                        setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
                        setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)
                    }}
                    noChange={isLoading}
                />
            </FormRow>
            <FormRow className='device'>
                <ValidationErrors name={ENDPOINT_DEVICE_NAME}>
                    <InputHelp text={language.get(ExtensionEndpointLanguage.DeviceHint)}>
                        <ComboBox
                            variant='outlined'
                            name={ENDPOINT_DEVICE_NAME}
                            label={language.get(ExtensionEndpointLanguage.ExtensionEndpoint_Device)}
                            loading={isLoadingDevices}
                            defaultOption={endpointDefaults.getNoDevice()}
                            options={availableDevices}
                            noChange={isLoading}
                            disabled={isEndpointDeviceDisabled}
                            onChange={(newValue: Nullable<ComboBoxItem<string>>): void => {
                                if (newValue && (isProfessionalUser || isWebexAgentOrSupervisorUser)) {
                                    setUserdetailsFromDevice(newValue.value)
                                }

                                trigger(ENDPOINT_AGENT_NAME)
                            }}
                            rules={deviceRules}
                        />
                    </InputHelp>
                </ValidationErrors>
            </FormRow>
            <FormRow className='agent'>
                <ValidationErrors name={ENDPOINT_AGENT_NAME}>
                    <ComboBox
                        variant='outlined'
                        name={ENDPOINT_AGENT_NAME}
                        label={language.get(ExtensionEndpointLanguage.ExtensionEndpoint_AndOrAgent)}
                        loading={isLoadingAgents}
                        defaultOption={endpointDefaults.getNoAgent()}
                        options={availableAgents}
                        noChange={isLoading}
                        disabled={viewOnly || isProfessionalUser}
                        onChange={(newValue: Nullable<ComboBoxItem<string>>): void => {
                            if (newValue && (isAgentUser || isWebexAgentOrSupervisorUser)) {
                                if (isWebexAgentOrSupervisorUser) {
                                    setUserdetailsFromDevice(newValue.value)
                                } else {
                                    setUserdetailsFromAgent(newValue.value)
                                }
                            }

                            trigger(ENDPOINT_DEVICE_NAME)
                        }}
                        rules={agentRules}
                    />
                </ValidationErrors>
            </FormRow>
        </div>
    )
}

export default ExtensionEndpoint
